// CodeMirror editor themes that match the rest of the kolilang web
// app's palette (the same `--bg-0/1/2`, `--fg-0/1/2`, `--accent` etc.
// CSS variables drive the toolbar / sidebar / output panel). Two
// bundles, one per page theme — each is `EditorView.theme(...)` for
// the editor chrome plus a `HighlightStyle` for SAS syntax tags.
//
// Replaces the off-the-shelf `oneDark` + `defaultHighlightStyle`
// pairing so the editor stops looking like a different app.

import { EditorView } from '@codemirror/view';
import type { Extension } from '@codemirror/state';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

interface Palette {
  bg0: string;
  bg1: string;
  bg2: string;
  fg0: string;
  fg1: string;
  fg2: string;
  accent: string;
  accentStrong: string;
  warn: string;
  err: string;
  ok: string;
  /// Translucent selection. Picked by hand so it reads on bg0 +
  /// stays subtle enough that it doesn't fight the syntax colors.
  selection: string;
  /// Background for in-document search matches.
  searchMatch: string;
  searchMatchSel: string;
}

const dark: Palette = {
  bg0: '#0e0e10',
  bg1: '#16161a',
  bg2: '#1c1c22',
  fg0: '#d7d7db',
  fg1: '#8a8a90',
  fg2: '#5a5a60',
  accent: '#7dd3fc',
  accentStrong: '#38bdf8',
  warn: '#fbbf24',
  err: '#f87171',
  ok: '#86efac',
  selection: 'rgba(125, 211, 252, 0.20)',
  searchMatch: 'rgba(251, 191, 36, 0.25)',
  searchMatchSel: 'rgba(251, 191, 36, 0.50)',
};

const light: Palette = {
  bg0: '#fafafa',
  bg1: '#f0f0f3',
  bg2: '#e2e2e8',
  fg0: '#1a1a1f',
  fg1: '#5a5a62',
  fg2: '#9094a0',
  accent: '#0284c7',
  accentStrong: '#0369a1',
  warn: '#b45309',
  err: '#b91c1c',
  ok: '#15803d',
  selection: 'rgba(2, 132, 199, 0.18)',
  searchMatch: 'rgba(180, 83, 9, 0.18)',
  searchMatchSel: 'rgba(180, 83, 9, 0.40)',
};

function makeChrome(p: Palette, isDark: boolean): Extension {
  return EditorView.theme(
    {
      '&': {
        color: p.fg0,
        backgroundColor: p.bg0,
      },
      '.cm-content': {
        caretColor: p.fg0,
      },
      '.cm-cursor, .cm-dropCursor': {
        borderLeftColor: p.fg0,
      },
      // Selection: focused + unfocused + native ::selection all
      // share the same translucent accent so the visible state is
      // consistent regardless of focus.
      '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
        backgroundColor: p.selection,
      },
      '.cm-activeLine': {
        backgroundColor: p.bg1,
      },
      '.cm-gutters': {
        backgroundColor: p.bg1,
        color: p.fg2,
        border: 'none',
      },
      '.cm-activeLineGutter': {
        backgroundColor: p.bg2,
        color: p.fg0,
      },
      '.cm-foldPlaceholder': {
        backgroundColor: p.bg2,
        color: p.fg1,
        borderColor: p.fg2,
      },
      '.cm-tooltip': {
        backgroundColor: p.bg2,
        border: `1px solid ${p.fg2}`,
        color: p.fg0,
      },
      '.cm-tooltip .cm-tooltip-arrow:before': {
        borderTopColor: p.fg2,
        borderBottomColor: p.fg2,
      },
      '.cm-tooltip .cm-tooltip-arrow:after': {
        borderTopColor: p.bg2,
        borderBottomColor: p.bg2,
      },
      '.cm-tooltip-autocomplete > ul > li[aria-selected]': {
        backgroundColor: p.bg1,
        color: p.accent,
      },
      '.cm-panels': {
        backgroundColor: p.bg1,
        color: p.fg0,
      },
      '.cm-panels.cm-panels-bottom': {
        borderTop: `1px solid ${p.bg2}`,
      },
      '.cm-panels.cm-panels-top': {
        borderBottom: `1px solid ${p.bg2}`,
      },
      '.cm-searchMatch': {
        backgroundColor: p.searchMatch,
        outline: `1px solid ${p.warn}`,
      },
      '.cm-searchMatch.cm-searchMatch-selected': {
        backgroundColor: p.searchMatchSel,
      },
      '.cm-diagnostic-error': { borderLeft: `3px solid ${p.err}` },
      '.cm-diagnostic-warning': { borderLeft: `3px solid ${p.warn}` },
      '.cm-diagnostic-info': { borderLeft: `3px solid ${p.accent}` },
    },
    { dark: isDark },
  );
}

function makeHighlight(p: Palette): HighlightStyle {
  return HighlightStyle.define([
    { tag: [t.keyword, t.controlKeyword, t.modifier, t.definitionKeyword], color: p.accent, fontWeight: 'bold' },
    { tag: t.string, color: p.ok },
    { tag: [t.number, t.bool, t.null], color: p.warn },
    { tag: [t.comment, t.lineComment, t.blockComment, t.docComment], color: p.fg2, fontStyle: 'italic' },
    { tag: [t.meta, t.processingInstruction], color: p.warn },
    { tag: t.invalid, color: p.err, textDecoration: 'underline' },
    { tag: [t.operator, t.compareOperator, t.logicOperator, t.arithmeticOperator], color: p.fg1 },
    { tag: t.punctuation, color: p.fg1 },
    { tag: t.variableName, color: p.fg0 },
    { tag: t.propertyName, color: p.fg0 },
    { tag: t.typeName, color: p.accentStrong },
  ]);
}

/// Editor theme bundle for the dark page theme — chrome plus
/// SAS-syntax highlight styles. Drop into the editor's
/// extensions array (e.g. through a Compartment so it can be
/// swapped at runtime).
export const kolilangDark: Extension = [
  makeChrome(dark, true),
  syntaxHighlighting(makeHighlight(dark), { fallback: true }),
];

export const kolilangLight: Extension = [
  makeChrome(light, false),
  syntaxHighlighting(makeHighlight(light), { fallback: true }),
];
